import { THEMES, THEME_LABELS, THEME_STORAGE_KEY, type ThemeName } from '@tarot/shared';

/**
 * 화면 테마 적용 — 회원 전용 기능.
 * 저장은 DB(users.theme, PUT /auth/me/theme)가 담당한다. 브라우저에는 아무것도 저장하지 않아
 * 캐시를 지우거나 기기를 바꿔도 로그인만 하면 설정이 그대로 따라온다.
 * 라이트 팔레트는 index.css 의 [data-theme='light'] 토큰 오버라이드가 담당.
 */
export const THEME_OPTIONS = THEMES.map((value) => ({ value, label: THEME_LABELS[value].ko }));

const mq = () => window.matchMedia('(prefers-color-scheme: light)');

/**
 * 모바일 브라우저의 주소창·하단 바 색을 현재 테마에 맞춘다 (`theme-color` 메타).
 * 고정값으로 두면 라이트 테마에서 화면은 흰데 위아래 크롬만 어두워 띠처럼 보인다.
 * 색은 index.css의 --color-night 토큰(테마별 단일 출처)에서 읽는다.
 */
function syncBrowserChrome(): void {
  const meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) return;
  const bg = getComputedStyle(document.documentElement).getPropertyValue('--color-night').trim();
  if (bg) meta.setAttribute('content', bg);
}

/**
 * html[data-theme] 반영. null = 기본(다크) — 비회원/로그아웃.
 * 적용한 값은 브라우저에 캐시해 다음 방문의 첫 페인트를 맞춘다 (index.html 부트 스크립트).
 * 캐시일 뿐 저장소가 아니다 — 진실의 원천은 DB(users.theme).
 */
export function applyTheme(theme: ThemeName | null): void {
  const resolved = theme === 'light' || (theme === 'system' && mq().matches) ? 'light' : 'dark';
  document.documentElement.dataset.theme = resolved;
  syncBrowserChrome();
  try {
    if (theme) localStorage.setItem(THEME_STORAGE_KEY, theme);
    else localStorage.removeItem(THEME_STORAGE_KEY); // 로그아웃·비회원은 기본으로 되돌린다
  } catch {
    /* 프라이빗 모드 등 저장 불가 — 깜빡임만 남고 기능은 그대로 */
  }
}

/** '시스템' 선택 시 OS 테마 변경 실시간 반영. 반환값은 해제 함수 */
export function watchSystemTheme(getActive: () => ThemeName | null): () => void {
  const m = mq();
  const onChange = () => {
    if (getActive() === 'system') applyTheme('system');
  };
  m.addEventListener('change', onChange);
  return () => m.removeEventListener('change', onChange);
}
